import { GridColumn } from '@progress/kendo-react-grid'
import classnames from 'classnames'
import { DateTimeCell } from 'components/content-pool/kendocells/datetimeCell'
import { HeaderComponent } from 'components/header/component'
import { RasaContext } from 'context'
import { BaseClientEntity } from 'generic/baseClientEntity'
import { Dataset } from 'generic/dataset'
import { EditableDropdownCell, EditCellProps } from 'generic/editCell'
import * as GenericRedux from 'generic/genericRedux'
import { PagingSource, RasaDataGrid } from 'generic/rasaDataGrid'
import React, { Component } from 'react'
import { Button } from 'reactstrap'
import { SharedKeys, SharedStore } from 'shared/data-layer/sharedStore'
import { ModalSize } from 'shared/modals/constants'
import { Loading } from '../../loading'
import { AddCommunityTopicModal } from './modal'
import './styles.css'

interface CommunityTopic {
  id: string
  community_id: string
  topic: string
  is_excluded: boolean
  is_archived: boolean
  created: string
  updated: string
}

type CommunityTopicsProps = GenericRedux.AllComponentPropsWithModal<CommunityTopic>

interface CommunityTopicsState {
  communityId: string
  editId: string
  editTopic: string
  forceReload: boolean
  topics: CommunityTopic[]
  loading: boolean
}

class CommunityTopicsComponent extends Component<CommunityTopicsProps, CommunityTopicsState> {
  public static contextType = RasaContext
  private entityName: string = 'communityTopics'
  private sharedStore: SharedStore

  constructor(props: CommunityTopicsProps) {
    super(props)
    this.state = {
      communityId: '',
      editId: null,
      editTopic: '',
      forceReload: false,
      topics: [],
      loading: true,
    }
  }

  public componentDidMount() {
    this.sharedStore = SharedStore.instance(this.context)
    Promise.all([
      this.sharedStore.getValue(SharedKeys.activeCommunity),
    ]).then(([activeCommunity]) => {
      this.getCommunityTopics(activeCommunity.communityId)
      .then((topics) => {
        this.setState({
          communityId: activeCommunity.communityId,
          topics,
          loading: false,
        })
      })
    })
  }

  public render() {
    return (
      this.state.loading ? <Loading size="64" /> :
      <div className="ai-image-preferences topic-exclusions">
        <div className="header-container">
          <AddCommunityTopicModal
            data={this.props.modals}
            closeModal={this.props.closeModal}
            createOrUpdate={this.createOrUpdate}
            saveModal={this.props.saveModal}
            updateModal={this.props.updateModal}
            topic={this.state.editTopic}
            size={ModalSize.Medium} />
          <HeaderComponent
            title={'Topic Exclusions'}
            subTitle={'Manage excluded topics for your community'}
          />
          <div className="section-right-side">
            <div className="section-button-container">
              <div className={classnames('add-sections-button-text-section')}>
                <Button
                  className={classnames('add-sections-button')}
                  onClick={() =>
                    this.props.openModal(AddCommunityTopicModal.key, null)}>
                  <span className="sections-button-text">
                    Add New Exclusion
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div>
          <RasaDataGrid
            entityName="communityTopics"
            datasetName="communityExcludedTopics"
            datasetParams={[]}
            editDefaultState={true}
            forceReload={this.state.forceReload}
            sortable={true}
            pageable={false}
            pagingSource={PagingSource.local}
            data={[]}>
            <GridColumn title="Topic Name" field="topic" width={400}
              cell={(props) => {
                return <td><div>{props.dataItem.topic}</div></td>
              }} />
            <GridColumn title="Created" field="created" width={200} cell={DateTimeCell} />
            <GridColumn width={100} cell={this.TopicEditCell} />
          </RasaDataGrid>
        </div>
      </div>
    )
  }

  protected TopicEditCell = (props: EditCellProps) => {
    return <EditableDropdownCell {...props} canEdit={true} onEdit={this.editClick}
            onArchive={this.onArchive} canArchive={true} />
  }

  private editClick = (dataItem: any) => {
    this.setState({
      editId: dataItem.id,
      editTopic: dataItem.topic,
    }, () => {
      this.props.openModal(AddCommunityTopicModal.key, dataItem)
    })
  }

  private onArchive = (props: any, event: any) => {
    this.context.entityMetadata.getEntityObject(this.entityName).then(
      (entityObject: BaseClientEntity) => {
        entityObject.setRecordId(this.state.communityId, props.dataItem.id)
        entityObject.archive()
        .then(() => {
          this.setState({
            forceReload: true,
          }, () => {
            this.getCommunityTopics(this.state.communityId)
            .then((topics) => {
              this.setState({
                topics,
                forceReload: false,
              })
            })
          })
        })
      })
  }

  private createOrUpdate = (payload: any) => {
    return this.context.entityMetadata
    .getEntityObject(this.entityName, this.state.communityId, null)
    .then((topicEntityObject: BaseClientEntity) => {
      topicEntityObject.setRecordId(this.state.communityId, this.state.editId ? this.state.editId : null)
      topicEntityObject.data.topic = payload.topic
      topicEntityObject.data.communityIdentifier = this.state.communityId
      return topicEntityObject.save()
        .then(() => {
          this.props.resetModal(AddCommunityTopicModal.key)
          this.setState({
            forceReload: true,
            editId: null,
            editTopic: '',
          }, () => {
            this.setState({ forceReload: false })
          })
          this.getCommunityTopics(this.state.communityId)
          .then((topics) => {
            this.setState({
              topics,
            })
          })
        })
    })
  }

  private getCommunityTopics = (community) => {
    return new Dataset()
    .loadCommunityDataset('communityExcludedTopics', community, [])
    .then((response) => {
      return response[0] || []
    })
  }
}

export const CommunityTopicsSettings = GenericRedux.registerNewComponentWithModals<CommunityTopic>(
  CommunityTopicsComponent,
  'community_topics',
  [AddCommunityTopicModal.key],
)